<template>
  <div class="flowbuilder-node-container" ref="containerEl">
    <div
      :class="{
        'flowbuilder-tests': hasTest,
        'flowbuilder-tests-expanded': hasTest
      }">
      <div v-if="hasTest" class="flowbuilder-tests-header">
        <span>Offer Split Test</span>
        <div class="flowbuilder-tests-status" :class="testStatusClasses">
          <div class="flowbuilder-tests-status-icon">
            <component :is="testStatusIcon" class="h-4 w-4" />
          </div>
          <div class="flowbuilder-tests-status-label">
            {{ testStatusLabel }}
            <a
              v-if="testIsActive"
              @click.stop="handleViewResults"
              class="flowbuilder-tests-view-results">
              View Results
            </a>
            <a
              v-if="testIsDraft"
              @click.stop="handleStartTest"
              class="flowbuilder-tests-view-results">
              Start test
            </a>
            <span v-if="testIsDraft" class="mx-1">-</span>
            <a
              v-if="testIsDraft"
              @click.stop="handleCancelTest"
              class="flowbuilder-tests-view-results">
              Cancel test
            </a>
          </div>
        </div>
      </div>
      <div class="flowbuilder-tests-content">
        <BaseNode
          :node="node"
          :icon="nodeIcon"
          @delete-node="emit('delete-node')"
          :metrics="hasTest ? null : metrics"
          ref="nodeEl"
          :classes="nodeBackground"
          :menu-items="menuItems"
          :context="hasTest ? 'CONTROL' : null">
          <template #context>
            {{ isFormLocal ? 'Show form:' : 'Pitch offer:' }}
          </template>

          <template v-if="offer">
            <div class="text-sm">{{ offer.name }}</div>

            <!-- Show offer preview inline when testing -->
            <div
              v-if="hasTest"
              class="-mx-4 mt-4 rounded bg-gray-50 px-4 py-3 dark:bg-gray-800">
              <OfferPreview
                :offer="offer"
                :is-form="isForm"
                :form-fields="formFields"
                :get-external-url="getExternalUrl"
                :process="process" />
            </div>
          </template>
          <template v-else>
            Select or create {{ isFormLocal ? 'a form' : 'an offer' }}
          </template>

          <template #hover v-if="offer && !hasTest">
            <OfferPreview
              :offer="offer"
              :is-form="isForm"
              :form-fields="formFields"
              :get-external-url="getExternalUrl"
              :process="process"
              :as-hover="true" />
          </template>
        </BaseNode>

        <!-- Render test variants dynamically -->
        <BaseNode
          v-for="(variant, index) in testVariants"
          :key="variant.id"
          :node="node"
          :icon="nodeIcon"
          :classes="'bg-cyan-50 dark:bg-cyan-950/30 ring-cyan-300/50 dark:ring-cyan-700/30'"
          :metrics="null"
          :menu-items="variantMenuItems(variant.id).value"
          :delete-label="'Remove this variant'"
          @delete-node="handleDeleteVariant(variant.id)"
          :context="`VARIANT #${index + 1}`"
          :variant-id="variant.id"
          :disable-drag="true">
          <template #context>
            {{ isFormLocal ? 'Show form:' : 'Pitch offer:' }}
          </template>

          <template v-if="offer">
            <div class="text-sm">{{ offer.name }}</div>

            <!-- Show variant preview inline -->
            <div
              class="-mx-4 mt-4 rounded bg-gray-50 px-4 py-3 dark:bg-gray-800">
              <OfferPreview
                :offer="getVariantOffer(variant)"
                :is-form="isForm"
                :form-fields="formFields"
                :get-external-url="getExternalUrl"
                :process="process"
                :variant-diff="
                  getOfferVariantDiff
                    ? getOfferVariantDiff(offer, variant)
                    : null
                " />
            </div>
          </template>
          <template v-else>
            Select or create {{ isFormLocal ? 'a form' : 'an offer' }}
          </template>
        </BaseNode>

        <div
          class="flowbuilder-tests-placeholder"
          v-if="hasTest && testIsDraft">
          <div class="flowbuilder-test-placeholder" @click="handleAddVariant">
            <div class="flowbuilder-test-placeholder-text">Add new variant</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Post-submit actions for forms -->
    <div v-if="redirectFunnel && !hasTest" class="relative">
      <div
        class="absolute -bottom-10 -left-10 -right-10 top-10 bg-slate-400/10">
        <div
          class="ml-4 mt-4 inline-block rounded-lg bg-slate-700 px-2 py-1 text-xs text-white opacity-70 dark:bg-slate-900">
          Post-Submit Actions For This Form
        </div>
      </div>
      <NodeRun
        :nodes="redirectFunnel.child"
        :inRedirectFunnel="true"
        :parent-node="redirectFunnel"
        @nodes-updated="handleRedirectNodesUpdated"
        @node-action="handleRedirectNodeAction" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, toRefs, watch, inject, ref, nextTick } from 'vue'
import BaseNode from './BaseNode.vue'
import NodeRun from './NodeRun.vue'
import OfferPreview from './OfferPreview.vue'
import { processLiquid } from '@rightmessage/shared-utils'
import { getNodeIcon } from '../../utils/nodeIcons'
import type { NodeOffer } from '../../types/nodes'
import type { Offer, Phrase } from '../../types'
import { PlayIcon, BeakerIcon } from '@heroicons/vue/24/outline'

/**
 * OfferNode Component
 *
 * Displays either a CTA (Call To Action) or a Form to the visitor.
 * Now supports A/B testing of offer content.
 *
 * Key features:
 * - Shows offer/form details with preview (inline when testing, on hover otherwise)
 * - Supports post-submit actions for forms (redirect funnel)
 * - Tracks conversion metrics
 * - Two types: 'offer' (CTA) or 'form'
 * - A/B testing support for offer variations
 */

interface Props {
  node: NodeOffer
  icon?: any
  CtaIcon: any
  FormIcon: any
}

const props = defineProps<Props>()

const emit = defineEmits(['delete-node'])

const { node } = toRefs(props)

// Inject dependencies from FlowBuilder
const templatePreview = inject('templatePreview', false)
const offers = inject('offers', ref([]))
const nodeProps = inject('nodeProps', ref({}))
const injectedPhrases = inject<Phrase[]>('phrases', [])
const getNodeMetrics = inject<(node: any) => any>('getNodeMetrics', () => null)

// A/B test state - will be populated from parent
const activeTest = inject<any>('getNodeTest', () => null)
const splitTestData = computed(() => {
  if (!node.value.offerId) return null
  return activeTest(node.value)
})

const hasTest = computed(() => {
  if (!splitTestData.value) return false
  // Show test UI if test exists and is not completed
  return splitTestData.value.status !== 'completed'
})

// Test status computed properties
const testIsDraft = computed(() => {
  return splitTestData.value?.status === 'draft'
})

const testIsActive = computed(() => {
  return splitTestData.value?.status === 'active'
})

const testStatusClasses = computed(() => {
  if (testIsActive.value) {
    return 'flowbuilder-tests-status-active'
  }
  return 'flowbuilder-tests-status-draft'
})

const testStatusIcon = computed(() => {
  if (testIsActive.value) {
    return PlayIcon
  }
  return BeakerIcon
})

const testStatusLabel = computed(() => {
  if (testIsActive.value) {
    return 'Test Running - '
  }
  return 'Test Draft - '
})

const testVariants = computed(() => {
  if (!splitTestData.value || !hasTest.value) return []
  return splitTestData.value.variants || []
})

// Inject adapter for handling node actions
const adapter = inject('adapter') as any

// Inject confirm dialog
const confirm = inject('confirm') as any

// Get variant diff utilities from adapter if available
const getOfferVariantDiff = adapter?.getOfferVariantDiff || null
const getVariantChangeStyles = adapter?.getVariantChangeStyles || null

// Look up the offer from the injected offers using node.offerId
const offer = computed(() => {
  if (!node.value.offerId) return null
  return offers.value.find((o: any) => o.id === node.value.offerId) || null
})

// Metrics from webapp
const metrics = computed(() => {
  // If a test is active, return zero metrics for now
  if (hasTest.value) {
    return [
      {
        label: 'Displays',
        value: 0
      },
      {
        label: isFormLocal.value ? 'Submits' : 'Clicks',
        value: 0
      },
      {
        label: 'Conversion Rate',
        value: '0.0%'
      }
    ]
  }

  // Get raw metrics from the flow metrics system
  const rawMetrics = getNodeMetrics(props.node)

  // If we have metrics from either legacy or enhanced reporting
  if (rawMetrics && Array.isArray(rawMetrics)) {
    // For enhanced reporting, the flowMetricsStore already formats metrics correctly
    // For legacy reporting, reportStore.metricsForOffer already returns correct format
    // Just need to handle the Clicks vs Submits label for forms
    return rawMetrics.map(metric => {
      if (metric.label === 'Clicks' && isFormLocal.value) {
        return { ...metric, label: 'Submits' }
      }
      return metric
    })
  }

  // Fallback to default metrics if no data available
  return [
    {
      label: 'Displays',
      value: '0'
    },
    {
      label: isFormLocal.value ? 'Submits' : 'Clicks',
      value: '0'
    },
    {
      label: 'Conversion Rate',
      value: '0.0%'
    }
  ]
})

// Get the appropriate icon for this node
const nodeIcon = computed(() => {
  // If an icon is passed as a prop, use it
  if (props.icon) return props.icon

  // Otherwise use the mapped icon based on form/CTA type
  return getNodeIcon(node.value)
})

// Node background color
const nodeBackground = computed(() => {
  return 'bg-cyan-100 dark:bg-cyan-950 ring-cyan-400/50 dark:ring-cyan-500/50 does-terminate'
})

// Get external URL function from nodeProps
const getExternalUrl = computed(() => {
  return nodeProps.value.getExternalUrl || ((url: string) => url)
})

/**
 * Determine if this is a form or a CTA
 * Forms have triggeredAction.type !== 'link'
 */
const isForm = computed(() => {
  if (!offer.value) return false
  return offer.value.triggeredAction?.type !== 'link'
})

/**
 * Use the desired type from node config, or detect from offer
 */
const isFormLocal = computed(() => {
  if (node.value.desiredType) {
    return node.value.desiredType === 'form'
  }
  return isForm.value
})

const process = (text: string) => {
  return processLiquid(text, injectedPhrases.value)
}

/**
 * Get form fields if this is a form
 */
const formFields = computed(() => {
  if (!isForm.value || !offer.value) return []
  return (offer.value.triggeredAction?.settings?.fields || []).filter(
    (f) => (f.label || f.displayType) && f.displayType !== 'submit' && f.displayType !== 'hidden'
  )
})

/**
 * Get the redirect funnel for forms (post-submit actions)
 */
const redirectFunnel = computed(() => {
  return offer.value?.triggeredAction?.settings?.redirectFunnel
})

// Get variant-specific offer data
const getVariantOffer = (variant: any) => {
  if (!offer.value) return null

  // Merge variant parts with original offer
  return {
    ...offer.value,
    parts: {
      ...offer.value.parts,
      ...(variant.parts || {})
    }
  }
}

// Menu items for this node type
const menuItems = computed(() => {
  const items = []

  // Only show "Test this offer" if no test exists
  if (offer.value && !hasTest.value) {
    items.push({
      label: isFormLocal.value ? 'Test this form' : 'Test this offer',
      icon:
        nodeProps.value.icons?.PresentationChartLineIcon ||
        'hugeicons:presentation-chart-01',
      command: () => {
        handleCreateTest()
      }
    })
  }

  return items
})

// Handle test creation
const handleCreateTest = async () => {
  // Notify adapter if available
  if (adapter?.onCreateTest && offer.value) {
    await adapter.onCreateTest({
      resourceType: 'offer',
      resourceId: node.value.offerId,
      offerId: node.value.offerId,
      offerName: offer.value.name
    })
  }
}

// Handle viewing test results
const handleViewResults = () => {
  if (adapter?.onViewTestResults && splitTestData.value) {
    adapter.onViewTestResults(splitTestData.value.pid)
  }
}

// Handle starting test
const handleStartTest = () => {
  console.log('handleStartTest called', {
    confirm: !!confirm,
    adapter: !!adapter,
    hasOnStartTest: !!adapter?.onStartTest,
    splitTestData: splitTestData.value
  })

  if (!confirm) {
    console.error('No confirm dialog available')
    // Fallback: use adapter's onStartTest directly if no confirm available
    if (adapter?.onStartTest && splitTestData.value) {
      if (
        window.confirm(
          "Once you start this test, you won't be able to make any edits while the test is running. Are you sure you want to start this test?"
        )
      ) {
        adapter.onStartTest(splitTestData.value.pid)
      }
    }
    return
  }

  if (!adapter?.onStartTest || !splitTestData.value) {
    console.error('Missing adapter.onStartTest or splitTestData', {
      hasOnStartTest: !!adapter?.onStartTest,
      splitTestData: splitTestData.value
    })
    return
  }

  confirm.require({
    message:
      "Once you start this test, you won't be able to make any edits while the test is running. Are you sure you want to start this test?",
    header: 'Start A/B Test?',
    acceptProps: { label: 'Start Test', severity: 'info' },
    rejectProps: { label: 'Cancel', outlined: true },
    accept: async () => {
      await adapter.onStartTest(splitTestData.value.pid)
    }
  })
}

// Handle adding a new variant
const handleAddVariant = async () => {
  if (adapter?.onAddTestVariant && splitTestData.value && offer.value) {
    const newVariant = {
      name: `Variant ${testVariants.value.length + 1}`,
      parts: {
        title: offer.value.parts?.title || '',
        description: offer.value.parts?.description || '',
        buttonText: offer.value.parts?.buttonText || '',
        imageUrl: offer.value.parts?.imageUrl || ''
      }
    }
    await adapter.onAddTestVariant(splitTestData.value.pid, newVariant)
  }
}

// Handle deleting a variant
const handleDeleteVariant = async (variantId: string) => {
  if (adapter?.onDeleteTestVariant && splitTestData.value) {
    await adapter.onDeleteTestVariant(splitTestData.value.pid, variantId)
  }
}

// Handle canceling test
const handleCancelTest = () => {
  console.log('handleCancelTest called', {
    confirm: !!confirm,
    adapter: !!adapter,
    hasOnDeleteTest: !!adapter?.onDeleteTest,
    splitTestData: splitTestData.value
  })

  if (!confirm) {
    console.error('No confirm dialog available')
    // Fallback: use native confirm if no confirm available
    if (adapter?.onDeleteTest && splitTestData.value) {
      if (
        window.confirm(
          'This will remove all variants and revert back to the content currently in the control. Are you sure you want to cancel this test?'
        )
      ) {
        adapter.onDeleteTest(splitTestData.value.pid)
      }
    }
    return
  }

  if (!adapter?.onDeleteTest || !splitTestData.value) {
    console.error('Missing adapter.onDeleteTest or splitTestData', {
      hasOnDeleteTest: !!adapter?.onDeleteTest,
      splitTestData: splitTestData.value
    })
    return
  }

  confirm.require({
    message:
      'This will remove all variants and revert back to the content currently in the control. Are you sure you want to cancel this test?',
    header: 'Cancel A/B Test?',
    acceptProps: { label: 'Cancel Test', severity: 'danger' },
    rejectProps: { label: 'Keep Test', outlined: true },
    accept: async () => {
      await adapter.onDeleteTest(splitTestData.value.pid)
    }
  })
}

// Handle cloning a variant
const handleCloneVariant = async (variantId: string) => {
  if (adapter?.onAddTestVariant && splitTestData.value && offer.value) {
    // Find the variant to clone
    const variantToClone = testVariants.value.find((v) => v.id === variantId)
    if (!variantToClone) return

    // Create a deep clone of the variant
    const clonedVariant = {
      name: `${variantToClone.name} (Copy)`,
      parts: JSON.parse(
        JSON.stringify(variantToClone.parts || offer.value.parts)
      )
    }

    await adapter.onAddTestVariant(splitTestData.value.pid, clonedVariant)
  }
}

// Menu items for variants
const variantMenuItems = (variantId: string) => {
  return computed(() => {
    const items = []

    // Clone variant option
    items.push({
      label: 'Clone variant',
      icon: 'hugeicons:copy',
      command: () => {
        handleCloneVariant(variantId)
      }
    })

    // Only show delete if there's more than one variant
    if (testVariants.value.length > 1) {
      items.push({
        separator: true
      })
      items.push({
        label: 'Delete variant',
        icon: 'hugeicons:trash',
        class: 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20',
        command: () => {
          handleDeleteVariant(variantId)
        }
      })
    }

    return items
  })
}

// Optional injected helpers - must be defined before use in watch
const ensureNodeUuids = inject<(funnel: any) => void>('ensureNodeUuids', null)
const generateUuid = inject<() => string>('generateUuid', () =>
  Math.random().toString(36).substr(2, 9)
)

/**
 * Process redirect funnel to ensure UUIDs and default content
 */
const processRedirectFunnel = (funnel: any) => {
  if (!funnel) return

  // If ensureNodeUuids is not available, manually ensure UUIDs
  if (ensureNodeUuids) {
    ensureNodeUuids(funnel)
  } else {
    // Fallback: manually ensure nodes have UUIDs
    const ensureUuid = (nodes: any[]) => {
      nodes.forEach((node) => {
        if (!node._uuid) {
          node._uuid = generateUuid()
        }
        // Handle nested nodes
        if (node.yes) ensureUuid(node.yes)
        if (node.no) ensureUuid(node.no)
        if (node.child) ensureUuid(node.child)
        if (node.split) {
          node.split.forEach((split: any) => {
            if (split.child) ensureUuid(split.child)
          })
        }
      })
    }

    if (funnel.child) {
      ensureUuid(funnel.child)
    }
  }

  // Add default thank you message if empty
  if (funnel.child && funnel.child.length === 0) {
    funnel.child.push({
      type: 'message',
      content: 'Thank you!',
      _uuid: generateUuid()
    })
  }
}

/**
 * Ensure all nodes in redirect funnel have UUIDs
 * This is needed for proper rendering and tracking
 */
watch(
  redirectFunnel,
  (newVal) => {
    if (newVal) {
      // Defer processing to ensure component is fully mounted
      nextTick(() => {
        processRedirectFunnel(newVal)
      })
    }
  },
  { immediate: true, deep: true }
)

/**
 * Handle updates to redirect funnel nodes
 */
const handleRedirectNodesUpdated = (nodes: any[]) => {
  if (redirectFunnel.value) {
    redirectFunnel.value.child = nodes
  }
}

/**
 * Handle actions from redirect funnel nodes
 * Don't bubble up click events from child nodes
 */
const handleRedirectNodeAction = (
  index: number,
  action: string,
  data?: any
) => {
  // Handle other actions but not clicks
  if (action !== 'click') {
    // You can emit or handle other actions here if needed
  }
  // Click actions from child nodes should not trigger the parent offer node
}
</script>

